import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useAuth } from '@/context/AuthContext';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const inputCls =
  "w-full px-3.5 py-2.5 rounded-xl border border-black/10 bg-black/[0.02] text-sm text-black placeholder-black/25 outline-none focus:border-black focus:bg-white transition";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/admin', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/admin', { replace: true });
    } catch {
      setError('E-mail ou senha inválidos. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-black/20" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Meia tela esquerda — foto */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="/images/bg-login.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      </div>

      {/* Meia tela direita — formulário */}
      <div className="w-full lg:w-1/2 flex flex-col justify-between bg-white px-10 py-12">
        <div className="flex-1 flex flex-col justify-center max-w-sm w-full mx-auto space-y-8">
          {/* Título */}
          <div>
            <h1 className="text-3xl font-semibold text-black tracking-tight">
              Painel administrativo
            </h1>
          </div>

          {/* Formulário */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-xs font-medium text-black/60">
                E-mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className={inputCls}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-xs font-medium text-black/60">
                Senha
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`${inputCls} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-black/25 hover:text-black/60 transition"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-500 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-1"
            >
              {isLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Entrando...</>
              ) : (
                "Entrar"
              )}
            </button>
          </form>
        </div>

        {/* Rodapé */}
        <div className="max-w-sm w-full mx-auto space-y-0.5 text-center">
          <p className="text-xs text-black/30 uppercase tracking-widest">
            DU NADA © {new Date().getFullYear()}
          </p>
          <p className="text-xs text-black/20 uppercase tracking-widest">
            Desenvolvido por Mídia Para Artistas
          </p>
        </div>
      </div>
    </div>
  );
}
